/**
 * Hook for managing user activity timeout and automatic token refresh.
 * 
 * Features:
 * - Tracks user activity (clicks, keystrokes, mouse movements, scroll, touch)
 * - Refreshes access token before it expires if user is active
 * - Logs out user after 30 minutes of inactivity
 * - Resets inactivity timer on any user interaction
 */
import { useEffect, useRef, useCallback } from 'react';
import api, {
  getRefreshToken,
  setTokens,
  clearTokens,
  clearSubAccountToken,
  getAccessToken,
  isPersistentSession,
  touchActivity,
  getLastActivity,
  getTokenExpiry as getStoredTokenExpiry,
  setTokenExpiry as setStoredTokenExpiry,
} from '@/lib/api';

// Inactivity timeout in milliseconds (30 minutes)
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

// Refresh token before expiry (refresh when 5 minutes remaining)
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

// How often to check if we need to refresh (every 1 minute)
const REFRESH_CHECK_INTERVAL_MS = 60 * 1000;

// Debounce activity events (don't update too frequently)
const ACTIVITY_DEBOUNCE_MS = 1000;

interface UseActivityTimeoutOptions {
  onLogout: () => void;
  enabled?: boolean;
}

/**
 * Refresh the access token using the refresh token.
 * Returns true if successful, false otherwise.
 */
async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await api.post('/auth/refresh', {
      refresh_token: refreshToken,
    });

    const { access_token, refresh_token, token_type, expires_in } = response.data;

    // Sem flag → preserva o storage ativo e grava token_expires_at no mesmo storage.
    setTokens({
      access_token,
      refresh_token: refresh_token || refreshToken, // Keep existing if not returned
      token_type,
      expires_in,
    });

    return true;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return false;
  }
}

/**
 * Parse JWT token to get expiration time.
 */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
}

export function useActivityTimeout({ onLogout, enabled = true }: UseActivityTimeoutOptions) {
  const lastActivityRef = useRef<number>(Date.now());
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);
  const lastActivityUpdateRef = useRef<number>(0);

  /**
   * Update the last activity timestamp (debounced).
   */
  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    
    // Debounce: only update if enough time has passed
    if (now - lastActivityUpdateRef.current < ACTIVITY_DEBOUNCE_MS) {
      return;
    }
    
    lastActivityRef.current = now;
    lastActivityUpdateRef.current = now;

    // Persiste no storage ativo da sessão (session ou local, conforme remember-me).
    touchActivity();
  }, []);

  /**
   * Check if user has been inactive for too long.
   */
  const checkInactivity = useCallback(() => {
    // "Manter Conectado": sessão persistente não expira por inatividade.
    if (isPersistentSession()) {
      return;
    }

    const storedActivity = getLastActivity();
    const lastActivity = storedActivity ? Number.parseInt(storedActivity, 10) : lastActivityRef.current;

    const inactiveTime = Date.now() - lastActivity;

    if (inactiveTime >= INACTIVITY_TIMEOUT_MS) {
      console.log('User inactive for 30 minutes, logging out...');
      clearTokens();
      clearSubAccountToken();
      onLogout();
    }
  }, [onLogout]);

  /**
   * Check if token needs refresh and refresh if necessary.
   */
  const checkAndRefreshToken = useCallback(async () => {
    if (isRefreshingRef.current) {
      return; // Already refreshing
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      return; // No token to refresh
    }

    // Get token expiry time
    let expiresAt = getStoredTokenExpiry();
    if (!expiresAt) {
      // Try to parse from JWT
      const jwtExpiry = getTokenExpiry(accessToken);
      if (jwtExpiry) {
        expiresAt = jwtExpiry.toString();
        setStoredTokenExpiry(jwtExpiry);
      }
    }

    if (!expiresAt) {
      return; // Can't determine expiry
    }

    const expiryTime = Number.parseInt(expiresAt, 10);
    const timeUntilExpiry = expiryTime - Date.now();

    // Refresh if less than 5 minutes until expiry
    if (timeUntilExpiry <= REFRESH_BEFORE_EXPIRY_MS && timeUntilExpiry > 0) {
      isRefreshingRef.current = true;
      
      try {
        const success = await refreshAccessToken();
        if (!success) {
          // Token refresh failed, check if user should be logged out
          checkInactivity();
        }
      } finally {
        isRefreshingRef.current = false;
      }
    }
  }, [checkInactivity]);

  /**
   * Set up activity listeners and timers.
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Initialize last activity
    const storedActivity = getLastActivity();
    if (!storedActivity) {
      touchActivity();
    }
    lastActivityRef.current = storedActivity ? Number.parseInt(storedActivity, 10) : Date.now();

    // Store initial token expiry if we have a token
    const accessToken = getAccessToken();
    if (accessToken && !getStoredTokenExpiry()) {
      const jwtExpiry = getTokenExpiry(accessToken);
      if (jwtExpiry) {
        setStoredTokenExpiry(jwtExpiry);
      }
    }

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, updateLastActivity, { passive: true });
    });

    // Set up inactivity check interval (every minute)
    inactivityTimerRef.current = setInterval(checkInactivity, 60 * 1000);

    // Set up token refresh check interval
    refreshIntervalRef.current = setInterval(checkAndRefreshToken, REFRESH_CHECK_INTERVAL_MS);

    // Initial checks
    checkInactivity();
    checkAndRefreshToken();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, updateLastActivity);
      });

      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }

      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [enabled, updateLastActivity, checkInactivity, checkAndRefreshToken]);

  /**
   * Manually trigger activity update (useful for programmatic interactions).
   */
  const triggerActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    touchActivity();
  }, []);

  /**
   * Force refresh the token now.
   */
  const forceRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      return false;
    }
    
    isRefreshingRef.current = true;
    try {
      return await refreshAccessToken();
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  return {
    triggerActivity,
    forceRefresh,
  };
}
